import React from 'react';
import { Helmet } from 'react-helmet-async';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Toaster } from 'react-hot-toast';
import { IRoute } from '../utils/routes/types';
import { routes } from '../utils/routes';
import { PageNotFound } from './pages/404';
import { AuthenticationGuard } from './components/Authenticated';
import PublicPages from 'app/components/PublicPage';
import GoToTopButton from 'app/components/GoToTop';
import {
  SITE_TITLE,
  SITE_DESCRIPTION,
  TITLE_TEMPLATE,
} from 'app/components/Seo/siteMeta';

export function App() {
  const { i18n } = useTranslation();
  return (
    <>
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: '#181e36',
            color: '#fff',
            borderRadius: '8px',
            border: '1px solid rgba(59, 130, 246, 0.3)',
          },
          success: {
            iconTheme: {
              primary: '#4ade80',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      <GoToTopButton />
      <BrowserRouter>
        {/*
          App-level head defaults.

          These are the fallbacks for any route that does not render its own
          <Seo>. Per-route title / description / canonical belong in <Seo>
          (src/app/components/Seo) — see the API docs in that file.

          The `<meta name="description" content="" />` that used to live here was
          an empty string. react-helmet-async resolves duplicate meta by name,
          so any route that mounted this without a page-level override would
          have shipped a blank description; and if this component ever rendered
          after a page's Helmet, it would have blanked a good one. Replaced with
          a real default computed from the dataset registry.
        */}
        <Helmet
          titleTemplate={TITLE_TEMPLATE}
          defaultTitle={SITE_TITLE}
          htmlAttributes={{ lang: i18n.language }}
        >
          <meta name="description" content={SITE_DESCRIPTION} />
        </Helmet>
        <Routes>
          <Route path="*" element={<PageNotFound />} />
          {routes.map((route: IRoute) => {
            let body: JSX.Element;
            if (route.isAuthenticated) {
              body = <AuthenticationGuard component={route.component} />;
            } else {
              body = <PublicPages route={route} />;
            }
            return (
              // Keyed by path, not `uuidv4()`. A fresh UUID on every render gave
              // every Route a new key each time App re-rendered, so React
              // unmounted and remounted the entire matched page — discarding all
              // component state and re-running every effect on the route.
              <Route key={route.path} path={route.path} element={<>{body}</>} />
            );
          })}
        </Routes>
      </BrowserRouter>
    </>
  );
}
